import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BarChart, PieChart } from "@mantine/charts";
import {
  Anchor,
  Badge,
  Box,
  Center,
  Grid,
  Group,
  Loader,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconFirstAidKit, IconHome, IconPhone } from "@tabler/icons-react";

import { OverviewCard } from "@pages/AdminDashboard/components/OverviewCard";

import { WithDashboardNavbar } from "@layouts/WithDashboardNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
  TruncatedCell,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { getCustomDateRange } from "@utils/getCustomDateRange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { DEFAULT_PAGINATION, ROLES } from "@constants/index";

import { useGetConsultationActivity } from "@hooks/queries/dashboard/useGetConsultationActivity";
import { useGetConsultationsTrend } from "@hooks/queries/dashboard/useGetConsultationsTrend";
import { useGetTotalAppointmentStatuses } from "@hooks/queries/dashboard/useGetTotalAppointmentStatuses";
import { useGetTotalConsultations } from "@hooks/queries/dashboard/useGetTotalConsultations";
import { useGetTotalConsultationStatuses } from "@hooks/queries/dashboard/useGetTotalConsultationStatuses";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { mq } from "@styles/Utilities.styled";

import { DateRangeSelect } from "../components/DateRangeSelect";


export const Consultations = () => {
  const { t, i18n } = useTranslation();

  const [dateRange, setDateRange] = useState<string | null>("last-7-days");
  const [customDateRange, setCustomDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [consultationActivityDateRange, setConsultationActivityDateRange] =
    useState<string | null>("last-7-days");
  const [
    customConsultationActivityDateRange,
    setCustomConsultationActivityDateRange,
  ] = useState<[Date | null, Date | null]>([null, null]);
  const [doctor, setDoctor] = useState<string | null>(null);
  const isMedium = useMediaQuery(`(${mq.medium})`);
  const [currentPage, setCurrentPage] = useState(1);
  const { tableRef, columnCount } = useTableColumnCount();

  const {
    data: consultations,
    isLoading: isConsultationsLoading,
    isSuccess: isConsultationsSuccess,
    isError: isConsultationsError,
  } = useGetTotalConsultations({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: consultationsTrend,
    isLoading: isConsultationsTrendLoading,
    isSuccess: isConsultationsTrendSuccess,
    isError: isConsultationsTrendError,
  } = useGetConsultationsTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: consultationStatuses,
    isLoading: isConsultationsStatusesLoading,
    isSuccess: isConsultationsStatusesSuccess,
    isError: isConsultationsStatusesError,
  } = useGetTotalConsultationStatuses({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: appointmentStatuses,
    isLoading: isAppointmentStatusesLoading,
    isSuccess: isAppointmentStatusesSuccess,
    isError: isAppointmentStatusesError,
  } = useGetTotalAppointmentStatuses({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: consultationActivityData,
    isLoading: isConsultationActivityLoading,
    isSuccess: isConsultationActivitySuccess,
    isError: isConsultationActivityError,
  } = useGetConsultationActivity({
    userId: doctor ?? "",
    pagination: {
      page: currentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
    dateRange: consultationActivityDateRange as string,
    ...(consultationActivityDateRange === "custom" &&
      getCustomDateRange(customConsultationActivityDateRange)),
  });
  const { data: doctorsData, isLoading: isDoctorsDataLoading } = useGetUsers({
    role: "doctor",
  });

  const doctors = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData]);

  const consultationActivityRows = useMemo(() => {
    return consultationActivityData?.newData?.map(item => (
      <Table.Tr key={item.id}>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14} c="black">
              {formatDateToString(item.date as Date)}
            </Text>
            <Text c="dimmed" fz={13}>
              {item.date?.toLocaleTimeString(i18n.language, {
                timeStyle: "short",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Anchor
            component={Link}
            to={`/patients/${item.patientId}/consultation/general/${item.id}`}
            ff={"var(--font-secondary)"}
            fz={14}
            c="accent"
            fw={500}>
            {item.cId}
          </Anchor>
        </Table.Td>
        <Table.Td ff={"var(--font-secondary)"}>{item.patientId}</Table.Td>
        <Table.Td>{t(item.type)}</Table.Td>
        <Table.Td>
          <TruncatedCell value={item.service} />
        </Table.Td>
        <Table.Td>
          <TruncatedCell value={item.physician} />
        </Table.Td>
        <Table.Td>
          <Badge
            w={"max-content"}
            fz={12}
            c={item.reportStatus === "incomplete" ? "red.8" : "green.8"}
            bg={item.reportStatus === "incomplete" ? "red.0" : "green.0"}>
            {t(item.reportStatus)}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge
            w={"max-content"}
            fz={12}
            c={item.billingStatus === "pending" ? "yellow.8" : "green.8"}
            bg={item.billingStatus === "pending" ? "yellow.0" : "green.0"}>
            {t(item.billingStatus)}
          </Badge>
        </Table.Td>
      </Table.Tr>
    ));
  }, [consultationActivityData, i18n.language]);

  const consultationsChartData = useMemo(
    () => [
      {
        name: "incomplete",
        value: consultationStatuses?.incomplete || 0,
        color: "orange.5",
      },
      {
        name: "complete",
        value: consultationStatuses?.complete || 0,
        color: "teal.5",
      },
    ],
    [consultationStatuses]
  );

  const appointmentsChartData = useMemo(
    () => [
      {
        name: "pending",
        value: appointmentStatuses?.pending || 0,
        color: "orange.5",
      },
      {
        name: "booked",
        value: appointmentStatuses?.booked || 0,
        color: "teal.5",
      },
      {
        name: "rejected",
        value: appointmentStatuses?.rejected || 0,
        color: "red.5",
      },
      {
        name: "cancelled",
        value: appointmentStatuses?.cancelled || 0,
        color: "gray.5",
      },
    ],
    [appointmentStatuses]
  );

  return (
    <WithSidebar hasDashboardNavbar hasBackground={false}>
      <WithDashboardNavbar userRole={ROLES.ADMIN}>
        <Group mt={"1rem"} justify="start">
          <Text fz={15}>{t("displaying-stats-for")}</Text>
          <DateRangeSelect
            dateRange={dateRange}
            setDateRange={setDateRange}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
          />
        </Group>
        <SimpleGrid cols={{ base: 2, md: 3, lg1: 4 }} mt={"2rem"}>
          <OverviewCard
            title={t("total-consultations")}
            amount={consultations?.total ?? 0}
            isActive
            archivedAmount={consultations?.archivedTotal ?? 0}
            isLoading={isConsultationsLoading}
            icon={
              <Group
                bg="accent.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconFirstAidKit
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-accent-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("homecare")}
            amount={consultations?.homecare ?? 0}
            archivedAmount={consultations?.archivedHomecare ?? 0}
            isActive
            isLoading={isConsultationsLoading}
            icon={
              <Group
                bg="blue.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconHome
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-blue-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("teleconsultation")}
            amount={consultations?.teleconsultation ?? 0}
            archivedAmount={consultations?.archivedTeleconsultation ?? 0}
            isActive
            isLoading={isConsultationsLoading}
            icon={
              <Group
                bg="green.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconPhone
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-green-6)"
                />
              </Group>
            }
          />
        </SimpleGrid>

        <Grid mt="3rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12, md: 12 }}>
            <Paper withBorder p="md">
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("distribution-homecare-teleconsultation")}
                </Title>
              </Group>

              {renderLoadingErrorData({
                isLoading: isConsultationsTrendLoading,
                isError: isConsultationsTrendError,
                isSuccess: isConsultationsTrendSuccess,
                dataLength: consultationsTrend?.length || 0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  // <Skeleton h={300} w={"100%"} mt={"1rem"} />
                  <Group h={300} mt={"1rem"} align="end" justify="space-evenly">
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"80%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"20%"} w={"8%"} />
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"70%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"30%"} w={"8%"} />
                    <Skeleton h={"60%"} w={"8%"} />
                    {isMedium && <Skeleton h={"50%"} w={"8%"} />}
                  </Group>
                ),
                dataComponent: (
                  <BarChart
                    mt={"1rem"}
                    h={300}
                    data={consultationsTrend || []}
                    // xAxisLabel="dates"
                    // yAxisLabel="Consultations"
                    dataKey="date"
                    withLegend
                    tooltipAnimationDuration={150}
                    withTooltip
                    legendProps={{ verticalAlign: "bottom" }}
                    series={[
                      { name: "homecare", color: "indigo.5" },
                      { name: "teleconsultation", color: "orange.5" },
                    ]}
                    tickLine="y"
                    tt={"capitalize"}
                  />
                ),
              })}
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 5, md: 4, lg1: 3.5, lg2: 3.5, lg3: 3 }}>
            <Paper
              withBorder
              p="md"
              w={{ base: "fit-content", md: "100%" }}
              miw={{ base: 290, md: "auto" }}>
              <Title order={3} fz={17} fw={500}>
                {t("consultation-status-breakdown")}
              </Title>

              {renderLoadingErrorData({
                isLoading: isConsultationsStatusesLoading,
                isSuccess: isConsultationsStatusesSuccess,
                isError: isConsultationsStatusesError,
                dataLength: consultationStatuses?.total || 0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={236}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={236}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  <Skeleton
                    h={236}
                    w={236}
                    mt={"1rem"}
                    radius={"100vmax"}
                    mx="auto"
                  />
                ),
                dataComponent: (
                  <PieChart
                    mt={"1rem"}
                    mx="auto"
                    withTooltip
                    size={236}
                    withLabelsLine={false}
                    labelsPosition="inside"
                    labelsType="percent"
                    withLabels
                    data={consultationsChartData}
                    tt={"capitalize"}
                    strokeWidth={5}
                  />
                ),
              })}

              <Group justify="space-around" mt="2.4rem">
                <Stack gap={7}>
                  <Group gap={7}>
                    <Box
                      w={7}
                      h={15}
                      bg="orange.5"
                      style={{ borderRadius: "100vmax" }}
                    />
                    <Text fz={12} c="dimmed">
                      {t("incomplete")}
                    </Text>
                  </Group>

                  {isConsultationsStatusesLoading ? (
                    <Skeleton h={15} w={40} />
                  ) : (
                    <Text fz={15} fw={500}>
                      {consultationStatuses?.incomplete || 0}
                    </Text>
                  )}
                </Stack>

                <Stack gap={7}>
                  <Group gap={7}>
                    <Box
                      w={7}
                      h={15}
                      bg="teal.5"
                      style={{ borderRadius: "100vmax" }}
                    />
                    <Text fz={12} c="dimmed">
                      {t("complete")}
                    </Text>
                  </Group>
                  {isConsultationsStatusesLoading ? (
                    <Skeleton h={15} w={40} />
                  ) : (
                    <Text fz={15} fw={500}>
                      {consultationStatuses?.complete || 0}
                    </Text>
                  )}
                </Stack>
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 5, lg1: 4.5, lg2: 4, lg3: 3.5 }}>
            <Paper
              withBorder
              p="md"
              w={{ base: "fit-content", md: "100%" }}
              miw={{ base: 290, md: "auto" }}>
              <Title order={3} fz={17} fw={500}>
                {t("appointment-status-breakdown")}
              </Title>

              {renderLoadingErrorData({
                isLoading: isAppointmentStatusesLoading,
                isSuccess: isAppointmentStatusesSuccess,
                isError: isAppointmentStatusesError,
                dataLength: appointmentStatuses?.total || 0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={236}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={236}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  <Skeleton
                    h={236}
                    w={236}
                    mt={"1rem"}
                    radius={"100vmax"}
                    mx="auto"
                  />
                ),
                dataComponent: (
                  <PieChart
                    mt={"1rem"}
                    mx="auto"
                    withTooltip
                    size={236}
                    withLabelsLine={false}
                    labelsPosition="inside"
                    labelsType="percent"
                    withLabels
                    data={appointmentsChartData}
                    strokeWidth={5}
                  />
                ),
              })}

              <Group justify="space-around" align="start" mt="2.4rem">
                <Stack gap={7}>
                  <Group gap={7}>
                    <Box
                      w={7}
                      h={15}
                      bg="orange.5"
                      style={{ borderRadius: "100vmax" }}
                    />
                    <Text fz={12} c="dimmed">
                      {t("pending")}
                    </Text>
                  </Group>
                  {isAppointmentStatusesLoading ? (
                    <Skeleton h={15} w={40} />
                  ) : (
                    <Text fz={15} fw={500}>
                      {appointmentStatuses?.pending || 0}
                    </Text>
                  )}
                </Stack>

                <Stack gap={7}>
                  <Group gap={7}>
                    <Box
                      w={7}
                      h={15}
                      bg="teal.5"
                      style={{ borderRadius: "100vmax" }}
                    />
                    <Text fz={12} c="dimmed">
                      {t("booked")}
                    </Text>
                  </Group>
                  {isAppointmentStatusesLoading ? (
                    <Skeleton h={15} w={40} />
                  ) : (
                    <Text fz={15} fw={500}>
                      {appointmentStatuses?.booked || 0}
                    </Text>
                  )}
                </Stack>

                <Stack gap={7}>
                  <Group gap={7}>
                    <Box
                      w={7}
                      h={15}
                      bg="red.5"
                      style={{ borderRadius: "100vmax" }}
                    />
                    <Text fz={12} c="dimmed">
                      {t("rejected")}
                    </Text>
                  </Group>
                  {isAppointmentStatusesLoading ? (
                    <Skeleton h={15} w={40} />
                  ) : (
                    <Text fz={15} fw={500}>
                      {appointmentStatuses?.rejected || 0}
                    </Text>
                  )}
                </Stack>

                <Stack gap={7}>
                  <Group gap={7}>
                    <Box
                      w={7}
                      h={15}
                      bg="gray.5"
                      style={{ borderRadius: "100vmax" }}
                    />
                    <Text fz={12} c="dimmed">
                      {t("cancelled")}
                    </Text>
                  </Group>
                  {isAppointmentStatusesLoading ? (
                    <Skeleton h={15} w={40} />
                  ) : (
                    <Text fz={15} fw={500}>
                      {appointmentStatuses?.cancelled || 0}
                    </Text>
                  )}
                </Stack>
              </Group>
            </Paper>
          </Grid.Col>

          {/* Helper column*/}
          <Grid.Col span={{ base: 1, md: 3 }}></Grid.Col>
        </Grid>

        <Grid mt="3rem" mb="8rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" w={{ base: "fit-content", md: "100%" }}>
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("consultation-activity")}
                </Title>
                <Group gap={"xs"}>
                  {/* <Button leftSection={<IconDownload size={16} />} disabled>
                    {t("export-csv")}
                  </Button> */}
                  <DateRangeSelect
                    dateRange={consultationActivityDateRange}
                    setDateRange={newRange => {
                      setConsultationActivityDateRange(newRange);
                      setCurrentPage(1); // Reset to first page when date range changes
                    }}
                    customDateRange={customConsultationActivityDateRange}
                    setCustomDateRange={newRange => {
                      setCustomConsultationActivityDateRange(newRange);
                    }}
                  />
                </Group>
              </Group>

              <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }} h={"100%"}>
                <Table
                  ref={tableRef}
                  withRowBorders={true}
                  mt={"1rem"}
                  miw={1280}
                  withTableBorder
                  highlightOnHover
                  highlightOnHoverColor="var(--clr-gray-100)">
                  <Table.Thead>
                    <Table.Tr bg={"var(--clr-gray-100)"}>
                      <Table.Th>{t("date-created")}</Table.Th>
                      <Table.Th>{t("consultation-id")}</Table.Th>
                      <Table.Th>{t("patient-id")}</Table.Th>
                      <Table.Th>{t("type")}</Table.Th>
                      <Table.Th>{t("service")}</Table.Th>
                      <Table.Th>
                        <Select
                          miw={200}
                          maw={275}
                          placeholder={t("doctor")}
                          data={doctors}
                          rightSection={
                            isDoctorsDataLoading ? <Loader size={16} /> : null
                          }
                          searchable
                          clearable
                          value={doctor}
                          onChange={(_, option) => {
                            setDoctor(option?.value);
                            setCurrentPage(1);
                          }}
                        />
                      </Table.Th>
                      <Table.Th>{t("report")}</Table.Th>
                      <Table.Th>{t("billing")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {renderLoadingErrorData({
                      isLoading: isConsultationActivityLoading,
                      isError: isConsultationActivityError,
                      isSuccess: isConsultationActivitySuccess,
                      dataLength:
                        consultationActivityData?.newData?.length || 0,
                      emptyDataComponent: (
                        <TableEmptyRow
                          colSpan={columnCount}
                          message={t("empty-data")}
                        />
                      ),
                      errorComponent: (
                        <TableErrorRow
                          colSpan={columnCount}
                          message={t("encountered-error")}
                        />
                      ),
                      loadingComponent: (
                        <TableSkeletonRows
                          columns={columnCount}
                          skeletonProps={{
                            widths: [
                              "md",
                              "md",
                              "md",
                              "md",
                              "lg",
                              "lg",
                              "md",
                              "md",
                            ],
                          }}
                        />
                      ),
                      dataComponent: consultationActivityRows,
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              <TablePagination
                paginationData={consultationActivityData?.pagination || {}}
                paginatedRows={consultationActivityData?.newData?.length || 0}
                onPageChange={setCurrentPage}
                isLoading={isConsultationActivityLoading}
              />
            </Paper>
          </Grid.Col>
        </Grid>
      </WithDashboardNavbar>
    </WithSidebar>
  );
};
